import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Card, Input, Select } from '@/admin-design/_common';
import { api } from '../api/client';

type Preset = { key: string; title: string; kind: string };

const KIND_LABEL: Record<string, string> = {
  hands: '손만',
  person: '인물',
  scene: '제품만',
};

type Composite = {
  id: number;
  product_id: number;
  preset: string;
  status: string;
  naver_image_url: string;
  error_message: string;
  created_at: string;
  product_name: string;
  source_image_url: string;
  price: number;
};

const TABS: Array<{ key: string; label: string }> = [
  { key: 'pending', label: '검수 대기' },
  { key: 'approved', label: '게시 대기' },
  { key: 'published', label: '게시됨' },
  { key: 'rejected', label: '거절' },
  { key: 'failed', label: '실패' },
];

const STATUS_TONE: Record<string, 'muted' | 'success' | 'warning' | 'info' | 'destructive'> = {
  pending: 'warning',
  approved: 'info',
  published: 'success',
  rejected: 'muted',
  failed: 'destructive',
};

export default function CompositesPage() {
  const qc = useQueryClient();
  const [tab, setTab] = useState('pending');
  // 스타일 선택. '' = 전체 순환, 'kind:scene' = 그 갈래 안에서 순환, 그 외 = 프리셋 고정.
  const [style, setStyle] = useState('');
  const [count, setCount] = useState('10');
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const list = useQuery({
    queryKey: ['composites', tab],
    queryFn: () => api.composites(tab) as Promise<Composite[]>,
  });

  const review = useMutation({
    mutationFn: (action: 'approve' | 'reject') =>
      api.reviewComposites({ ids: [...selected], action }),
    onSuccess: (_d, action) => {
      setMsg({ ok: true, text: `${selected.size}건 ${action === 'approve' ? '승인' : '거절'}했습니다.` });
      setSelected(new Set());
      qc.invalidateQueries({ queryKey: ['composites'] });
    },
    onError: (e: unknown) => setMsg({ ok: false, text: String(e) }),
  });

  const publish = useMutation({
    mutationFn: () => api.createJob('publish_composite', {}),
    onSuccess: () => setMsg({ ok: true, text: '게시 잡을 큐에 넣었습니다. Jobs 에서 진행 상황을 볼 수 있습니다.' }),
    onError: (e: unknown) => setMsg({ ok: false, text: String(e) }),
  });

  const presets = useQuery({
    queryKey: ['composite-presets'],
    queryFn: () => api.compositePresets() as Promise<Preset[]>,
  });

  const generate = useMutation({
    mutationFn: () => {
      const limit = Math.max(1, Math.min(50, parseInt(count, 10) || 10));
      const payload: Record<string, unknown> = { limit };
      if (style.startsWith('kind:')) payload.kind = style.slice(5);
      else if (style) payload.preset = style;
      return api.createJob('compose_images', payload);
    },
    onSuccess: () => setMsg({ ok: true, text: '합성 잡을 큐에 넣었습니다. 장당 90초 남짓 걸립니다.' }),
    onError: (e: unknown) => setMsg({ ok: false, text: String(e) }),
  });

  const rows = list.data ?? [];
  const toggle = (id: number) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  return (
    <div className="space-y-4">
      <Card className="shadow-sm">
        <Card.Header>
          <Card.Title>이미지 합성 검수</Card.Title>
          <Card.Description>
            도매처 대표이미지를 GPU 서버에서 연출 합성한 결과입니다. 승인한 것만 네이버
            대표이미지로 교체되며, 기존 대표이미지는 추가이미지로 남습니다.
          </Card.Description>
        </Card.Header>
        <div className="px-6 py-4 flex flex-wrap items-center gap-2">
          {TABS.map((t) => (
            <Button
              key={t.key}
              size="sm"
              variant={tab === t.key ? 'primary' : 'secondary'}
              onClick={() => { setTab(t.key); setSelected(new Set()); }}
            >
              {t.label}
            </Button>
          ))}
          <div className="flex-1" />
          <Button size="sm" variant="secondary" loading={publish.isPending} onClick={() => publish.mutate()}>
            승인분 게시
          </Button>
        </div>

        <div className="px-6 pb-4 flex flex-wrap items-end gap-3 border-t border-border pt-4">
          <div>
            <label className="block text-xs text-muted-foreground mb-1">연출 스타일</label>
            <Select
              value={style}
              onValueChange={setStyle}
              className="w-60"
              options={[
                { value: '', label: '전체 순환' },
                { value: 'kind:scene', label: '제품만 — 갈래 순환' },
                { value: 'kind:hands', label: '손만 — 갈래 순환' },
                { value: 'kind:person', label: '인물 — 갈래 순환' },
                ...(presets.data ?? []).map((p) => ({
                  value: p.key,
                  label: `${KIND_LABEL[p.kind] ?? p.kind} · ${p.title}`,
                })),
              ]}
            />
          </div>
          <div>
            <label className="block text-xs text-muted-foreground mb-1">건수</label>
            <Input
              type="number"
              min={1}
              max={50}
              className="w-24"
              value={count}
              onChange={(e) => setCount(e.target.value)}
            />
          </div>
          <Button size="sm" variant="primary" loading={generate.isPending} onClick={() => generate.mutate()}>
            새로 합성
          </Button>
          <p className="text-xs text-muted-foreground">
            장당 약 90초. 사람이 나오는 연출은 건강기능식품 광고 심의 소지가 있어 제품만/손만 쪽이 안전합니다.
          </p>
        </div>

        {msg && (
          <div className={`px-6 pb-3 text-sm ${msg.ok ? 'text-muted-foreground' : 'text-destructive'}`}>
            {msg.text}
          </div>
        )}
      </Card>

      {tab === 'pending' && rows.length > 0 && (
        <Card className="shadow-sm">
          <div className="px-6 py-3 flex items-center gap-3">
            <span className="text-sm text-muted-foreground">{selected.size}건 선택됨</span>
            <Button size="sm" variant="secondary" onClick={() => setSelected(new Set(rows.map((r) => r.id)))}>
              전체 선택
            </Button>
            <div className="flex-1" />
            <Button
              size="sm"
              variant="primary"
              disabled={selected.size === 0}
              loading={review.isPending}
              onClick={() => review.mutate('approve')}
            >
              승인
            </Button>
            <Button
              size="sm"
              variant="secondary"
              disabled={selected.size === 0}
              loading={review.isPending}
              onClick={() => review.mutate('reject')}
            >
              거절
            </Button>
          </div>
        </Card>
      )}

      {list.isLoading && <p className="text-sm text-muted-foreground">불러오는 중…</p>}
      {!list.isLoading && rows.length === 0 && (
        <p className="text-sm text-muted-foreground">해당 상태의 합성 이미지가 없습니다.</p>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {rows.map((r) => {
          const isSel = selected.has(r.id);
          return (
            <Card
              key={r.id}
              className={`shadow-sm overflow-hidden ${isSel ? 'ring-2 ring-primary' : ''}`}
            >
              {/* 원본과 합성본을 나란히 — 라벨이 뭉개졌는지, 손이 이상한지는 비교해야 보인다 */}
              <div className="grid grid-cols-2 gap-px bg-border">
                <img
                  src={r.source_image_url}
                  alt="원본"
                  className="w-full aspect-square object-contain bg-white"
                  loading="lazy"
                />
                <button
                  type="button"
                  onClick={() => tab === 'pending' && toggle(r.id)}
                  className="block"
                  aria-label={`합성 이미지 ${r.id} 선택`}
                >
                  <img
                    src={api.compositeImageURL(r.id)}
                    alt="합성"
                    className="w-full aspect-square object-contain bg-white"
                    loading="lazy"
                  />
                </button>
              </div>
              <div className="px-4 py-3 space-y-1">
                <div className="flex items-center gap-2">
                  <Badge variant={STATUS_TONE[r.status] ?? 'muted'}>{r.status}</Badge>
                  <span className="text-xs text-muted-foreground">{r.preset}</span>
                  <div className="flex-1" />
                  {tab === 'pending' && (
                    <input
                      type="checkbox"
                      checked={isSel}
                      onChange={() => toggle(r.id)}
                      aria-label={`합성 ${r.id} 선택`}
                    />
                  )}
                </div>
                <p className="text-sm font-medium leading-snug line-clamp-2">{r.product_name}</p>
                <p className="text-xs text-muted-foreground">
                  #{r.product_id} · {r.price.toLocaleString()}원
                </p>
                {r.error_message && (
                  <p className="text-xs text-destructive break-all">{r.error_message}</p>
                )}
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
